import { Fragment, useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { createPortal } from "react-dom";
import type VideoJs from "video.js";
import { motion } from "motion/react";
import { CONFIG } from "./config";
import { loadPlayer, lockLandscapeForFullscreen } from "./player-core";
import { isInAppShell, showEscape, STANDALONE_RECORDINGS_URL, getIdentityForHandoff } from "./identity";

type Recording = {
  id: string;
  title: string | null;
  created: string | null;
  duration: number | null;
  thumbnail: string | null;
  manifest: string | null;
};

type Status = "loading" | "ready" | "empty" | "unauthorized" | "error";

// Shown as real h:mm:ss / m:ss rather than rounded minutes, so it matches
// the time the player itself displays exactly — "8 min" next to a player
// reading 7:50 just looks broken (confirmed: the API's duration and the
// actual playable duration agree precisely; only the rounding differed).
function formatDuration(seconds: number | null): string {
  if (!seconds || seconds <= 0) return "";
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  const two = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${two(m)}:${two(sec)}` : `${m}:${two(sec)}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

// Cloudflare auto-names a live input's recordings things like
// "noisy-thunder-ae1a 07 Sep 26 20:19 UTC" — meaningless to a customer, so
// the displayed title is derived from when the class actually ran instead.
// A genuine human-set name (renamed in the Stream dashboard) is kept as-is.
function isAutoGeneratedName(name: string | null): boolean {
  if (!name) return true;
  return /^[a-z]+-[a-z]+-[0-9a-f]{4}\s/i.test(name.trim());
}

function displayTitle(rec: { title: string | null; created: string | null }): string {
  if (!isAutoGeneratedName(rec.title)) return rec.title as string;
  if (!rec.created) return "Class recording";
  return new Date(rec.created).toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function formatTimeOfDay(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export default function RecordingsView() {
  const [status, setStatus] = useState<Status>("loading");
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [initialIndex, setInitialIndex] = useState(0);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [handoffUrl, setHandoffUrl] = useState<string | null>(null);

  // One real video.js instance for the whole page, created once and never
  // destroyed between selections — its DOM node gets physically relocated
  // into whichever row is currently active (see selectRow below), instead
  // of living in one fixed slot the viewer has to scroll back to. Cheaper
  // and less disruptive than disposing/recreating a full player (with its
  // Chromecast/AirPlay setup) on every tap, and avoids a black-frame
  // flash from tearing down and rebuilding the tech each time.
  const playerRef = useRef<ReturnType<typeof VideoJs> | null>(null);
  const playerHostRef = useRef<HTMLDivElement | null>(null);
  const rowSlotRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const cardRefs = useRef<Record<number, HTMLElement | null>>({});
  // Kept current via 'timeupdate' (see selectRow) and read directly at tap
  // time, rather than through React state, since updating state at
  // multiple-times-a-second would re-render on every tick for no reason —
  // the anchors' hrefs are patched imperatively instead.
  const currentTimeRef = useRef(0);
  const escapeLinkRefs = useRef<HTMLAnchorElement[]>([]);

  // Standalone (?t=) means we're on the foreign pages.dev domain already —
  // there's no Kajabi identity to re-check there, and no point offering an
  // "escape the app" link when we're not in the app to begin with.
  const tFromUrl = new URLSearchParams(window.location.search).get("t");
  const isStandalone = !!tFromUrl;
  // In-app webview: no viewport coordinates available (see inlineHere).
  const inApp = isInAppShell() && !isStandalone;

  useEffect(() => {
    if (!CONFIG.handoffWorkerUrl) {
      setStatus("error");
      return;
    }

    const request = isStandalone
      ? fetch(`${CONFIG.handoffWorkerUrl}/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ t: tFromUrl }),
        }).then((r) => (r.ok ? r.json() : null))
      : fetch(`${CONFIG.handoffWorkerUrl}/recordings`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(getIdentityForHandoff()),
        }).then((r) => (r.ok ? r.json() : null));

    request
      .then((data) => {
        if (isStandalone) {
          if (!data?.valid || !data?.entitlementVerified || !data?.streamToken) {
            setStatus("unauthorized");
            return;
          }
          if (data.url) setHandoffUrl(data.url);

          let list: Recording[] = Array.isArray(data.recordings) ? data.recordings : [];
          let idx = list.findIndex((r) => r.id === data.videoUid);

          // The full list only fails to come back if listing videos itself
          // errored server-side — the one recording this link points at is
          // still playable (its own streamToken already came back above),
          // so fall back to a single-item list rather than a dead end.
          if (idx === -1) {
            list = [
              {
                id: data.videoUid,
                title: null,
                created: null,
                duration: null,
                thumbnail: null,
                manifest: `https://${CONFIG.customerSubdomain}/${data.streamToken}/manifest/video.m3u8`,
              },
              ...list,
            ];
            idx = 0;
          }

          setRecordings(list);
          setInitialIndex(idx);
          setStatus(list.length ? "ready" : "empty");
        } else {
          const list: Recording[] = (data && data.recordings) || [];
          setRecordings(list);
          setInitialIndex(0);
          setStatus(list.length ? "ready" : "empty");
        }
      })
      .catch(() => setStatus("error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refreshes the "watch in fullscreen" link for whichever recording is
  // currently active — same proven plain-<a-href> mechanism as the live
  // player, minted per item since each needs its own handoff session token.
  // Skipped entirely in standalone mode: there's no Kajabi identity there to
  // mint a fresh one with, and the button is redundant once already outside
  // the app.
  function refreshEscapeLink(videoUid: string) {
    if (isStandalone || !CONFIG.handoffWorkerUrl) return;
    fetch(`${CONFIG.handoffWorkerUrl}/mint`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...getIdentityForHandoff(), videoUid }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.url) setHandoffUrl(data.url);
      })
      .catch(() => {});
  }

  // Patches every registered "watch externally" anchor's href with the
  // exact current playback position, read from a plain data attribute (the
  // base handoff URL) rather than React state — called on every
  // 'timeupdate' tick, so by the time someone actually taps the link it
  // already carries a fresh-enough timestamp with zero click-time JS (the
  // proven-working mechanism needs a plain <a href>, nothing computed at
  // click time, or the app's foreign-domain hand-off silently breaks).
  function syncEscapeLinkPositions() {
    const seconds = Math.floor(currentTimeRef.current);
    for (const el of escapeLinkRefs.current) {
      const base = el.dataset.baseHref;
      if (!base) continue;
      el.href = base + (base.includes("?") ? "&" : "?") + "start=" + seconds;
    }
  }

  function registerEscapeLink(el: HTMLAnchorElement | null) {
    if (!el) return;
    if (!escapeLinkRefs.current.includes(el)) escapeLinkRefs.current.push(el);
  }

  async function ensurePlayer(): Promise<ReturnType<typeof VideoJs> | null> {
    if (playerRef.current) return playerRef.current;
    const videojs = await loadPlayer();
    if (playerRef.current) return playerRef.current;

    // The host div is created here, once, detached from React's own tree —
    // selectRow physically moves this exact element between row slots
    // afterward, so video.js's internal DOM (built inside it) never gets
    // torn down and rebuilt on every tap.
    if (!playerHostRef.current) {
      playerHostRef.current = document.createElement("div");
    }

    const videoEl = document.createElement("video");
    videoEl.className = "video-js vjs-fluid vjs-big-play-centered";
    videoEl.setAttribute("playsinline", "");
    videoEl.setAttribute("x-webkit-airplay", "allow");
    videoEl.setAttribute("preload", "auto");
    videoEl.setAttribute("crossorigin", "anonymous");
    playerHostRef.current.appendChild(videoEl);

    const player = videojs(videoEl, {
      controls: true,
      fluid: true,
      // Confirmed live: video.js's inactivity auto-hide makes the first tap
      // on any control get swallowed by the underlying <video> instead of
      // the button. Controls stay visible/clickable always instead.
      inactivityTimeout: 0,
      playsinline: true,
      techOrder: ["chromecast", "html5"],
      // Fullscreen/PiP are blocked at the WebView level inside Kajabi's
      // app (dead buttons exactly where showEscape is true).
      controlBar: showEscape ? { fullscreenToggle: false, pictureInPictureToggle: false } : undefined,
      plugins: { chromecast: {}, airPlay: {} },
      html5: {
        vhs: { overrideNative: false, limitRenditionByPlayerDimensions: true },
        nativeAudioTracks: true,
        nativeVideoTracks: true,
      },
    });
    playerRef.current = player;

    player.on("timeupdate", () => {
      currentTimeRef.current = player.currentTime() || 0;
      syncEscapeLinkPositions();
    });

    return player;
  }

  // shouldPlay is false for the initial auto-select: landing on the
  // recordings page (especially inside the app) must NOT start playing a
  // class at you unprompted — it just puts the player in place, ready. A
  // real tap, or arriving from a handoff link (where continuing playback
  // seamlessly is the entire point), is what actually starts playback.
  async function selectRow(index: number, seekTo?: number, shouldPlay = true) {
    const rec = recordings[index];
    if (!rec || !rec.manifest) return;

    const player = await ensurePlayer();
    if (!player) return;

    // In list mode the destination slot lives inside the modal, which only
    // exists after this state change renders — attachPlayerToSlot (an
    // effect keyed on activeIndex) does the actual DOM move afterward.
    const slot = rowSlotRefs.current[index];
    if (slot && playerHostRef.current && playerHostRef.current.parentElement !== slot) {
      slot.appendChild(playerHostRef.current);
    }

    setActiveIndex(index);
    requestAnimationFrame(() => {
      document.querySelector(".cfp-modal")?.scrollIntoView({ block: "center", behavior: "smooth" });
    });

    if (player.currentSrc() !== rec.manifest) {
      // A stale "start=" from whatever was playing before must not carry
      // over to a brand new video until the first real timeupdate tick.
      currentTimeRef.current = 0;
      syncEscapeLinkPositions();
      player.poster(rec.thumbnail || "");
      player.src({ src: rec.manifest, type: "application/x-mpegURL" });
      player.load();

      if (seekTo && seekTo > 0) {
        player.one("loadedmetadata", () => player.currentTime(seekTo));
      }
    }

    const playPromise = shouldPlay ? player.play() : null;
    playPromise?.catch(() => {
      // autoplay can be blocked (e.g. no prior user gesture on this exact
      // element yet) — controls are still there for a manual tap.
    });

    // Best-effort only, standalone only (a real browser tab, not the
    // in-app WebView where this is blocked anyway) — resuming exactly
    // where the tap left off and autoplaying are both reliable; jumping
    // straight to fullscreen is NOT: browsers require a direct, fresh user
    // gesture for the Fullscreen API, and a page load/navigation doesn't
    // count, so this can silently no-op depending on the browser. The
    // player's own fullscreen button is still right there either way.
    // Screen Orientation lock has the same "must be fullscreen first"
    // requirement and isn't supported on iOS Safari at all.
    if (isStandalone && seekTo !== undefined) {
      playPromise
        ?.then(() => {
          try {
            const hostEl = playerHostRef.current?.querySelector(".video-js") as (HTMLElement & { requestFullscreen?: () => Promise<void> }) | null;
            hostEl?.requestFullscreen?.().then(() => lockLandscapeForFullscreen()).catch(() => {});
          } catch {
            // Fullscreen API not available/permitted here — the manual
            // fullscreen button in the control bar still works.
          }
        })
        .catch(() => {});
    }

    refreshEscapeLink(rec.id);
  }

  useEffect(() => {
    return () => {
      playerRef.current?.dispose();
      playerRef.current = null;
    };
  }, []);

  // Only the solo/hand-off page auto-starts. In list mode nothing is
  // selected until tapped — opening the recordings page must not pop a
  // modal at you or start playing a class unprompted.
  useEffect(() => {
    if (isStandalone && status === "ready" && recordings.length > 0 && activeIndex === null) {
      // The link carries &start=<seconds> — the exact position playback was
      // at inside the app when "Watch in Fullscreen" was tapped — so this
      // resumes there instead of restarting at 0.
      const startParam = new URLSearchParams(window.location.search).get("start");
      const seekTo = startParam !== null ? Math.max(0, parseFloat(startParam) || 0) : undefined;
      selectRow(initialIndex, seekTo, seekTo !== undefined);
      // The modal opens by itself here, so the viewer never scrolled to
      // this class — put the list behind it in the right place too, or
      // closing the modal drops them at the top with no idea where the
      // class they just watched actually sits.
      requestAnimationFrame(() => {
        cardRefs.current[initialIndex]?.scrollIntoView({ block: "center" });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, recordings, initialIndex]);

  // The modal's player slot only exists once the modal has rendered, so
  // the actual DOM relocation of the live player happens here, after that
  // render — not inside selectRow, which runs before it.
  useEffect(() => {
    if (activeIndex === null) return;
    const slot = rowSlotRefs.current[activeIndex];
    const host = playerHostRef.current;
    if (slot && host && host.parentElement !== slot) {
      slot.appendChild(host);
    }
  }, [activeIndex]);

  // Inside the Kajabi app the page itself never scrolls: the native app
  // scrolls its own container and sizes the WebView to the FULL content
  // height. So window.scrollY is always 0 and innerHeight is the entire
  // list — meaning neither position:fixed nor scroll-anchoring can know
  // where the viewer is actually looking. The one thing we do know is
  // which card was just tapped, and a tapped card is on screen by
  // definition — so the modal is anchored to that card's position.
  const [overlayBox, setOverlayBox] = useState<{ top: number; centred?: boolean } | null>(null);
  // A known visible centre means we can show a properly centred modal
  // even in the app; without it we fall back to the in-flow panel.
  const canCentreInApp = overlayBox?.centred === true;
  const [soloStarted, setSoloStarted] = useState(false);

  // If the page can scroll itself, we're in a normal browser and
  // position:fixed genuinely centres on the viewport — the nicest result,
  // and it stays put while scrolling. If it can't scroll (content-sized
  // WebView, where the native app does the scrolling), fixed is
  // meaningless, so fall back to anchoring at the tapped card.
  // Deterministic, not a scroll measurement: inside the app webview
  // position:fixed can't be trusted at all (Android WebViews resolve fixed
  // against the document, not the viewport, when the host page's viewport
  // meta isn't set up for it — and we don't control Kajabi's meta tag), so
  // there we always anchor to the tapped card, which is on screen by
  // definition. Everywhere else, real fixed centring is used.
  // Where on the page to put the modal, inside the app webview.
  //
  // The page can't read scroll position there (the native app scrolls and
  // the WebView is sized to the whole content), but the tap itself carries
  // both coordinate systems: clientY is where the finger landed in the
  // document, screenY is where it landed on the physical screen. The
  // difference between them IS the document offset of the top of the
  // screen — so a single tap reveals the visible region without any scroll
  // information at all.
  function captureOverlayBox(e: { clientY: number; screenY: number } | null, cardEl: HTMLElement | null) {
    if (!isInAppShell()) {
      setOverlayBox(null);
      return;
    }

    const screenH = window.screen?.height || 0;
    if (e && screenH > 0) {
      const visibleTop = e.clientY + (window.scrollY || 0) - e.screenY;
      const centre = visibleTop + screenH / 2;
      const docH = document.documentElement.scrollHeight || 0;
      // Clamp so a bad reading can never park it outside the document.
      const clamped = Math.min(Math.max(centre, screenH / 2), Math.max(docH - screenH / 2, screenH / 2));
      setOverlayBox({ top: Math.round(clamped), centred: true });
      return;
    }

    // No usable tap (opened programmatically) — fall back to the card,
    // which is on screen by definition when someone taps it.
    if (!cardEl) {
      setOverlayBox(null);
      return;
    }
    const rect = cardEl.getBoundingClientRect();
    setOverlayBox({ top: Math.max(0, rect.top + (window.scrollY || 0)), centred: false });
  }

  // Opening the modal pushes a history entry so the device/browser back
  // button closes it, rather than navigating away from the lesson
  // entirely (which inside the Kajabi app means backing out of the class).
  const pushedHistoryRef = useRef(false);

  function openModalHistory() {
    if (pushedHistoryRef.current) return;
    try {
      history.pushState({ cfpModal: true }, "");
      pushedHistoryRef.current = true;
    } catch {
      // history unavailable in this context — modal still works, back
      // button just won't close it.
    }
  }

  function closeModal() {
    playerRef.current?.pause();
    // Put the viewer back where they were: without this, closing drops
    // them at the top of a long list with no idea which class they'd just
    // been watching — worst of all on the hand-off page, where the modal
    // opened by itself and they never scrolled there in the first place.
    const closingIndex = activeIndex;
    // Scroll the card into view BEFORE starting the close animation, and
    // instantly rather than smoothly: the shared-layout animation returns
    // the modal into that card, so if it isn't on screen yet the whole
    // animation plays off-screen and you land somewhere unrelated. A
    // smooth scroll also gets interrupted by the layout animation.
    if (closingIndex !== null) {
      cardRefs.current[closingIndex]?.scrollIntoView({ block: "center", behavior: "auto" });
    }
    requestAnimationFrame(() => setActiveIndex(null));
    if (pushedHistoryRef.current) {
      pushedHistoryRef.current = false;
      try {
        history.back();
      } catch {
        // nothing to unwind
      }
    }
  }

  useEffect(() => {
    function onPopState() {
      // Back was pressed while the modal was open — our entry is already
      // gone, so just tear the modal down without pushing/popping again.
      if (pushedHistoryRef.current) {
        pushedHistoryRef.current = false;
        playerRef.current?.pause();
        setActiveIndex(null);
      }
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const overlayText: Record<Exclude<Status, "ready">, [string, string]> = {
    loading: ["Loading…", "Fetching past class recordings."],
    empty: ["No recordings yet", "Check back after the next live class."],
    unauthorized: [
      "Sign in required",
      "You'll need an active subscription to this course to watch — open this from inside your course, not directly.",
    ],
    error: ["Couldn't load recordings", "Try refreshing the page."],
  };

  if (status !== "ready") {
    const [head, body] = overlayText[status];
    return (
      <div id="cf-recordings">
        <div className="cfp-frame">
          <div className="cfp-stage">
            <div className="cfp-overlay">
              <p className="cfp-overlay-head">{head}</p>
              <p className="cfp-overlay-body">{body}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Arrived from "Watch in Fullscreen" — show ONLY that recording, big and
  // playing. Landing back on another scrollable list here would defeat the
  // whole point of the hand-off.
  function startSoloPlayback() {
    const player = playerRef.current;
    setSoloStarted(true);
    if (!player) return;
    // All inside the tap's user-activation window, so these are permitted.
    player.play()?.catch(() => {});
    try {
      const el = playerHostRef.current?.querySelector(".video-js") as
        | (HTMLElement & { requestFullscreen?: () => Promise<void> })
        | null;
      el?.requestFullscreen?.()
        .then(() => lockLandscapeForFullscreen())
        .catch(() => {});
    } catch {
      // Fullscreen unavailable here — playback still starts.
    }
  }

  const activeRec = activeIndex !== null ? recordings[activeIndex] : null;

  return (
    <div id="cf-recordings">
      {isStandalone && <p className="cfp-solo-course">{CONFIG.courseName}</p>}
      <div className="cfp-cardgrid">
        {recordings.map((rec, index) => {
          const meta = [formatDate(rec.created), formatTimeOfDay(rec.created), formatDuration(rec.duration)]
            .filter(Boolean)
            .join(" · ");
          // Inside the app webview there is no viewport to position
          // against (the native app owns scrolling and the WebView is
          // sized to the whole content), so the player is rendered inline
          // in the flow right where the card is — nothing to centre,
          // nothing to clip.
          const inlineHere = inApp && !canCentreInApp && activeIndex === index;
          return (
            <Fragment key={rec.id}>
            <motion.button
              type="button"
              className="cfp-card"
              hidden={inlineHere}
              ref={(el) => { cardRefs.current[index] = el as HTMLElement | null; }}
              layoutId={`rec-${rec.id}`}
              onClick={(e) => { captureOverlayBox(e, e.currentTarget as HTMLElement); openModalHistory(); selectRow(index); }}
              aria-label={`Play ${displayTitle(rec)}`}
            >
              <motion.div className="cfp-card-media" layoutId={`rec-media-${rec.id}`}>
                {rec.thumbnail ? (
                  <img className="cfp-card-thumb" loading="lazy" alt="" src={rec.thumbnail} />
                ) : (
                  <div className="cfp-card-thumb-placeholder" />
                )}
                <span className="cfp-card-play" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="26" height="26">
                    <path fill="currentColor" d="M8 5v14l11-7z" />
                  </svg>
                </span>
              </motion.div>
              <div className="cfp-card-info">
                <p className="cfp-card-title">{displayTitle(rec)}</p>
                <p className="cfp-card-meta">{meta}</p>
              </div>
            </motion.button>

            {inlineHere && (
              <div className="cfp-inline-panel">
                {/* Dims the whole page behind the panel so this still
                    reads as a modal, while the panel itself stays in the
                    normal flow (the only way it can't land off-screen in
                    the app's WebView, which has no viewport to centre
                    against). */}
                {createPortal(
                  <div
                    className="cfp-inline-backdrop"
                    style={{ height: document.documentElement.scrollHeight }}
                    onClick={closeModal}
                  />,
                  document.body
                )}
                <div
                  className="cfp-row-slot"
                  ref={(el) => {
                    rowSlotRefs.current[index] = el;
                    const host = playerHostRef.current;
                    if (el && host && host.parentElement !== el) el.appendChild(host);
                  }}
                />
                <div className="cfp-modal-bar">
                  <div className="cfp-modal-meta">
                    <p className="cfp-modal-title">{displayTitle(rec)}</p>
                    <p className="cfp-modal-sub">{meta}</p>
                  </div>
                  {showEscape && (
                    <a
                      ref={registerEscapeLink}
                      className="cfp-action cfp-action-fs"
                      href={handoffUrl ?? STANDALONE_RECORDINGS_URL}
                      data-base-href={handoffUrl ?? STANDALONE_RECORDINGS_URL}
                      rel="noopener noreferrer"
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                        <path fill="currentColor" d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
                      </svg>
                      <span>Watch in Fullscreen</span>
                    </a>
                  )}
                  <button type="button" className="cfp-inline-close" onClick={closeModal} aria-label="Close">
                    Close
                  </button>
                </div>
              </div>
            )}
            </Fragment>
          );
        })}
      </div>

      {/* Radix Dialog handles the portal, backdrop, Escape key, focus
          trapping, scroll locking and ARIA wiring — all of which this was
          hand-rolling (and missing the a11y half of). motion still drives
          the shared-layout animation from card to modal. */}
      <Dialog.Root
        open={(!inApp || canCentreInApp) && activeRec !== null && activeIndex !== null}
        onOpenChange={(open) => { if (!open) closeModal(); }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="cfp-modal-backdrop" />
          <Dialog.Content
            className="cfp-modal-shell"
            style={overlayBox ? { position: "absolute", top: overlayBox.top, left: 0, right: 0, transform: "translateY(-50%)" } : undefined}
            aria-describedby={undefined}
          >
            {activeRec && activeIndex !== null && (
              <motion.div className="cfp-modal" layoutId={`rec-${activeRec.id}`}>
                <Dialog.Title className="cfp-visually-hidden">{displayTitle(activeRec)}</Dialog.Title>

                <motion.div className="cfp-modal-media" layoutId={`rec-media-${activeRec.id}`}>
                  <div
                    className="cfp-row-slot"
                    ref={(el) => {
                      rowSlotRefs.current[activeIndex] = el;
                      // Radix mounts this after the open-state effect has
                      // already run, so relocate the live player here, the
                      // moment the slot actually exists.
                      const host = playerHostRef.current;
                      if (el && host && host.parentElement !== el) el.appendChild(host);
                    }}
                  />
                  {isStandalone && !soloStarted && (
                    <button type="button" className="cfp-solo-start" onClick={startSoloPlayback}>
                      <span className="cfp-solo-start-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="34" height="34">
                          <path fill="currentColor" d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                      <span className="cfp-solo-start-label">Tap to play fullscreen</span>
                    </button>
                  )}
                </motion.div>

                <div className="cfp-modal-bar">
                  <div className="cfp-modal-meta">
                    <p className="cfp-modal-title">{displayTitle(activeRec)}</p>
                    <p className="cfp-modal-sub">
                      {[formatDate(activeRec.created), formatTimeOfDay(activeRec.created), formatDuration(activeRec.duration)]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>

                  {showEscape && (
                    <a
                      ref={registerEscapeLink}
                      className="cfp-action cfp-action-fs"
                      href={handoffUrl ?? STANDALONE_RECORDINGS_URL}
                      data-base-href={handoffUrl ?? STANDALONE_RECORDINGS_URL}
                      rel="noopener noreferrer"
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                        <path fill="currentColor" d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
                      </svg>
                      <span>Watch in Fullscreen</span>
                    </a>
                  )}
                </div>

                <Dialog.Close className="cfp-modal-close" aria-label="Close">
                  <svg viewBox="0 0 24 24" width="20" height="20">
                    <path fill="currentColor" d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </Dialog.Close>
              </motion.div>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
